/**
 * Parameter Change History
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { ParameterHistory, ParameterType } from '../../types/parameters';

const ChangeHistory: React.FC = () => {
  const [paramType, setParamType] = useState('');
  const [limit, setLimit] = useState(50);

  // Fetch history
  const { data: history, isLoading } = useQuery<ParameterHistory[]>({
    queryKey: ['parameterHistory', paramType, limit],
    queryFn: async () => {
      const response = await api.getParameterHistory({
        param_type: paramType || undefined,
        limit,
      });
      return response.data;
    },
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Parameter Change History
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track all parameter changes for audit and compliance
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Parameter Type</InputLabel>
            <Select
              value={paramType}
              label="Parameter Type"
              onChange={(e) => setParamType(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value={ParameterType.GLOBAL}>Global</MenuItem>
              <MenuItem value={ParameterType.STORE}>Store</MenuItem>
              <MenuItem value={ParameterType.PRODUCT}>Product</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Limit"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            inputProps={{ min: 10, max: 500, step: 10 }}
          />
        </Grid>
      </Grid>

      {/* History Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date/Time</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Parameter Name</TableCell>
              <TableCell>Old Value</TableCell>
              <TableCell>New Value</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Changed By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : history && history.length > 0 ? (
              history.map((entry) => (
                <TableRow key={entry.history_id}>
                  <TableCell>{formatDate(entry.changed_at)}</TableCell>
                  <TableCell>
                    <Chip label={entry.param_type} size="small" />
                  </TableCell>
                  <TableCell>{entry.param_name}</TableCell>
                  <TableCell>
                    <Typography variant="caption" component="pre">
                      {entry.old_value ? JSON.stringify(entry.old_value, null, 2) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" component="pre">
                      {JSON.stringify(entry.new_value, null, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell>{entry.change_reason || '-'}</TableCell>
                  <TableCell>{entry.changed_by}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No change history found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ChangeHistory;
